import type {
  Alert,
  AssetCurrentState,
  DashboardSummary,
  DeviceCurrentState,
  EarthquakeOverlayResponse,
  GeocodeAddressInput,
  GeocodeResult,
  MapAlertMarker,
  MapAlertsFilter,
  MapAssetsFilter,
  MapAssetMarker,
  MapSite,
  MapWorkItemMarker,
  MapWorkItemsFilter,
  NwsWeatherOverlayResponse,
  PlacesSearchInput,
  PlacesSearchResult,
  RoutesEtaInput,
  RoutesEtaResult,
  SiteBoundary,
  SiteBoundaryUpdateInput,
  SiteModelResponse,
  WeatherContextResponse,
} from "@conatus/contracts";
import { api } from "@/lib/api";

const tenantBase = (tenantId: string): string => `/tenants/${encodeURIComponent(tenantId)}`;

/**
 * Typed wrappers around the dashboard / map Lambda (`dashboardMapFn`) and the
 * current-state endpoints added to `assetsFn` / `devicesFn`.
 */
export const DashboardMapApi = {
  dashboard: {
    getSummary(tenantId: string) {
      return api<DashboardSummary>(`${tenantBase(tenantId)}/dashboard/summary`);
    },
    getRisk(tenantId: string) {
      return api<DashboardSummary["risk"] & { tenantId: string; generatedAt: string }>(
        `${tenantBase(tenantId)}/dashboard/risk`,
      );
    },
    getRecentAlerts(tenantId: string) {
      return api<{ tenantId: string; items: Alert[] }>(
        `${tenantBase(tenantId)}/dashboard/recent-alerts`,
      );
    },
    getIntegrationHealth(tenantId: string) {
      return api<{
        tenantId: string;
        healthy: number;
        degraded: number;
        failing: number;
        disabled: number;
      }>(`${tenantBase(tenantId)}/dashboard/integration-health`);
    },
  },

  map: {
    getSites(tenantId: string) {
      return api<{ items: MapSite[] }>(`${tenantBase(tenantId)}/map/sites`);
    },
    getSiteAssets(tenantId: string, siteId: string) {
      return api<{ items: MapAssetMarker[] }>(
        `${tenantBase(tenantId)}/map/sites/${encodeURIComponent(siteId)}/assets`,
      );
    },
    listAssets(tenantId: string, filter: Partial<MapAssetsFilter> = {}) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filter)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
      const qs = params.toString();
      return api<{ items: MapAssetMarker[]; nextCursor?: string }>(
        `${tenantBase(tenantId)}/map/assets${qs ? `?${qs}` : ""}`,
      );
    },
    listAlerts(tenantId: string, filter: Partial<MapAlertsFilter> = {}) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filter)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
      const qs = params.toString();
      return api<{ items: MapAlertMarker[]; nextCursor?: string }>(
        `${tenantBase(tenantId)}/map/alerts${qs ? `?${qs}` : ""}`,
      );
    },
    listWorkItems(tenantId: string, filter: Partial<MapWorkItemsFilter> = {}) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filter)) {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      }
      const qs = params.toString();
      return api<{ items: MapWorkItemMarker[]; nextCursor?: string }>(
        `${tenantBase(tenantId)}/map/work-items${qs ? `?${qs}` : ""}`,
      );
    },
    getSiteModel(tenantId: string, siteId: string) {
      return api<SiteModelResponse>(
        `${tenantBase(tenantId)}/map/sites/${encodeURIComponent(siteId)}/model`,
      );
    },
    getNwsContext(tenantId: string, siteId: string) {
      return api<NwsWeatherOverlayResponse>(
        `${tenantBase(tenantId)}/map/sites/${encodeURIComponent(siteId)}/nws-context`,
      );
    },
    getEarthquakes(tenantId: string, siteId: string) {
      return api<EarthquakeOverlayResponse>(
        `${tenantBase(tenantId)}/map/sites/${encodeURIComponent(siteId)}/earthquakes`,
      );
    },
  },

  places: {
    search(tenantId: string, input: PlacesSearchInput) {
      return api<PlacesSearchResult>(`${tenantBase(tenantId)}/map/places-search`, {
        method: "POST",
        body: input,
      });
    },
  },

  routes: {
    getEta(tenantId: string, siteId: string, input: RoutesEtaInput) {
      return api<RoutesEtaResult>(
        `${tenantBase(tenantId)}/map/sites/${encodeURIComponent(siteId)}/routes/eta`,
        { method: "POST", body: input },
      );
    },
  },

  weather: {
    getContext(tenantId: string, siteId: string) {
      return api<WeatherContextResponse>(
        `${tenantBase(tenantId)}/sites/${encodeURIComponent(siteId)}/weather-context`,
      );
    },
  },

  geocode: {
    lookup(tenantId: string, input: GeocodeAddressInput) {
      return api<GeocodeResult>(`${tenantBase(tenantId)}/map/geocode`, {
        method: "POST",
        body: input,
      });
    },
  },

  boundary: {
    get(tenantId: string, siteId: string) {
      return api<SiteBoundary | null>(
        `${tenantBase(tenantId)}/sites/${encodeURIComponent(siteId)}/boundary`,
      );
    },
    update(tenantId: string, siteId: string, input: SiteBoundaryUpdateInput) {
      return api<SiteBoundary>(
        `${tenantBase(tenantId)}/sites/${encodeURIComponent(siteId)}/boundary`,
        { method: "PUT", body: input },
      );
    },
  },

  currentState: {
    getAsset(tenantId: string, assetId: string) {
      return api<AssetCurrentState>(
        `${tenantBase(tenantId)}/assets/${encodeURIComponent(assetId)}/current-state`,
      );
    },
    getDevice(tenantId: string, deviceId: string) {
      return api<DeviceCurrentState>(
        `${tenantBase(tenantId)}/devices/${encodeURIComponent(deviceId)}/current-state`,
      );
    },
  },
};
